import type { ReactNode } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { CCS_PALETTE } from "./theme";

export interface LoginShellProps {
  appName: string;
  subtitle?: string;
  children: ReactNode;
}

/**
 * Minimal UI Kit–style login wrapper.
 *
 * Layout: centred card on a very subtle grey background.
 * Top section: brand-coloured pill + title.
 * Bottom section: the form slot.
 */
export function LoginShell({ appName, subtitle, children }: LoginShellProps) {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        bgcolor: "grey.100",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 420,
          borderRadius: 3,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        {/* Brand header stripe */}
        <Box
          sx={{
            px: 4,
            pt: 4,
            pb: 3,
            background: `linear-gradient(135deg, ${CCS_PALETTE.purpleTint} 0%, #ede9fe 100%)`,
            borderBottom: "1px solid",
            borderColor: "divider",
            textAlign: "center",
          }}
        >
          {/* Logo mark */}
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: CCS_PALETTE.katanaPurple,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 2,
            }}
          >
            <Typography
              sx={{
                color: "#fff",
                fontWeight: 800,
                fontSize: "1.25rem",
                lineHeight: 1,
                fontFamily: "Public Sans, sans-serif",
              }}
            >
              K
            </Typography>
          </Box>

          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: "text.primary", mb: 0.5 }}
          >
            {appName}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>

        {/* Form area */}
        <Box sx={{ px: 4, py: 3.5 }}>{children}</Box>
      </Paper>
    </Box>
  );
}
